import { withoutEntries } from '@app/utils/common';
import { equals } from '@app/utils/equals';
import { ConnectedList } from '@app/utils/lists/connected-list/connected-list';
import { ConnectedListValue } from '@app/utils/lists/connected-list/connected-list-value';
import { PagedList } from '@app/utils/lists/paged-list/paged-list';
import { PagedListImpl } from '@app/utils/lists/paged-list/paged-list-impl';
import { loggerFor } from '@app/utils/logger';
import { StringKeys } from '@app/utils/string-keys';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

export class ConnectedListImpl<T> implements ConnectedList<T> {
  public all$: BehaviorSubject<T[]>;
  public value$: Observable<ConnectedListValue<T>>;

  public available: PagedList<T>;
  public selected: PagedList<T>;

  private destroyed$ = new Subject<void>();
  private log = loggerFor('ConnectedList');

  constructor() {
    this.available = new PagedListImpl({
      name: 'available',
    });

    this.selected = new PagedListImpl({
      name: 'selected',
    });

    this.all$ = new BehaviorSubject([]);
    this.value$ = combineLatest([this.available.page$, this.selected.page$]).pipe(
      takeUntil(this.destroyed$),
      map(([availablePage, selectedPage]): ConnectedListValue<T> => {
        return {
          available: availablePage,
          selected: selectedPage,
        };
      })
    );
  }

  destroy(): this {
    this.destroyed$.next();

    return this;
  }

  draw(): this {
    this.log.trace('draw()');

    this.available.draw();
    this.selected.draw();

    return this;
  }

  select(element: T): this {
    this.available.remove(element);
    this.selected.push(element);

    this.selected.goTo(this.selected.lastInsertLocation);

    return this;
  }

  selectAllFiltered(): this {
    const newSelected = this.selected.entries.concat(this.available.filteredEntries);
    const newAvailable = withoutEntries(this.available.entries, this.available.filteredEntries);
    const newFilteredSelected = this.selected.filteredEntries.concat(this.available.filteredEntries);

    this.selected.updateEntries(newSelected);
    this.selected.updateFilteredEntries(newFilteredSelected);
    this.available.updateEntries(newAvailable);
    this.available.updateFilteredEntries([]);

    return this;
  }

  sortBy(properties: StringKeys<T>[]): this {
    if (properties == null) {
      properties = [];
    }

    this.available.sortBy(properties);
    this.selected.sortBy(properties);

    return this;
  }

  unselect(element: T): this {
    this.selected.remove(element);
    this.available.push(element);

    this.available.goTo(this.available.lastInsertLocation);

    return this;
  }

  unselectAllFiltered(): this {
    const newSelected = withoutEntries(this.selected.entries, this.selected.filteredEntries);
    const newAvailable = this.available.entries.concat(this.selected.filteredEntries);
    const newFilteredAvailable = this.available.filteredEntries.concat(this.selected.filteredEntries);

    this.selected.updateEntries(newSelected);
    this.selected.updateFilteredEntries([]);
    this.available.updateEntries(newAvailable);
    this.available.updateFilteredEntries(newFilteredAvailable);

    return this;
  }

  update(possibilities: T[], selectedElements: T[]): this {
    const availableValues = withoutEntries(possibilities, selectedElements, equals);

    this.available.updateEntries(availableValues);
    this.selected.updateEntries(selectedElements);
    this.all$.next(availableValues.concat(selectedElements));

    return this;
  }

  updateFiltered(elements: T[], selected: T[]): this {
    this.available.updateFilteredEntries(elements);
    this.selected.updateFilteredEntries(selected);

    return this;
  }

  updatePageSize(pageSize: number): this {
    this.available.updatePageSize(pageSize);
    this.selected.updatePageSize(pageSize);

    return this;
  }
}
